import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Header.scss";
import {
  IoBookOutline,
  IoSearchOutline,
  IoCloseCircleOutline,
  IoExitOutline,
  IoPersonSharp
} from "react-icons/io5";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Songbook } from "../../../@core/models";
import { Auth } from "../../../@core/auth";
import debounce from "lodash/debounce";
import RestClient from "../../../@core/restClient";
import Spinner from "../../widgets/UI/Spinner";

type queryState = [query: string, setQuery: (query: string) => void];

type SongbooksState = [
  songbooks: Songbook[],
  setSongbooks: (songbooks: Songbook[]) => void
];

type IsOpenState = [isOpen: boolean, setIsOpen: (isOpen: boolean) => void];

const Header = () => {
  const queryRef = useRef<any>();
  const [query, setQuery]: queryState = useState<string>("");
  const [songList, setSongList] = useState<any[]>([]);
  const [isOpen, setIsOpen]: IsOpenState = useState(false);
  const [categoryPrompt, setCategoryPrompt] = useState(false);
  const [chooseCategory, setChoseCategory] = useState(false);
  const [category, setCategory] = useState("");
  const [categoryList, setCategoryList] = useState<any>([]);
  const [categoryName, setCategoryName] = useState("");
  const navigate = useNavigate();
  const [person, setPerson] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // CHEKING AUTHENTICATION ON LOAD AND ON VISIBILITY CHANGE
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Auth.isAuthenticated) {
          setPerson(true);
        } else {
          setPerson(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // CHECK AUTHENTICATION ON MOUNT
  useEffect(
    function () {
      if (Auth.isAuthenticated) {
        setPerson(true);
      } else {
        setPerson(false);
      }
    },
    [localStorage.getItem("User")]
  );
  //CHEKING AUTHENTICATION EVERY 30 MIN

  useEffect(() => {
    const interval = setInterval(
      () => {
        if (Auth.isAuthenticated) {
          setPerson(true);
        } else {
          setPerson(false);
        }
      },
      30 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  const goHome = () => {
    setIsOpen(false);
    navigate("/");
  };

  const deferredSetQueryHandler = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    }, 500),
    []
  );
  useEffect(() => {
    (async () => {
      try {
        const response = await RestClient.getCategoryList();
        setCategoryList(response || []);
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(
    function () {
      (async () => {
        if (!query && !category) {
          setIsOpen(false);
          return;
        }
        try {
          setIsLoading(true);
          const response = await RestClient.getSongListByQuery(
            query,
            category,
            true
          );
          setSongList(response || []);
          setIsOpen(true);
          setChoseCategory(false);
        } catch (error) {
          console.log(error);
        }

        setIsLoading(false);
      })();
    },
    [query, category]
  );

  const selectSongbook = (songbookId: any, songId: any) => {
    setSongList([]);
    queryRef.current.value = "";
    setQuery("");
    navigate(`/songbook/${songbookId}/song/${songId}`);
    setIsOpen(false);
    setCategoryPrompt(false);
    setChoseCategory(false);
    setCategory("");
    setCategoryName("");
  };

  const goToCabinet = () => {
    navigate("/cabinet");
  };

  useEffect(() => {
    if (isOpen || categoryPrompt || chooseCategory) {
      // Named function so we can reference it for removal
      const handleClick = () => {
        setIsOpen(false);
        setCategoryPrompt(false);
        setChoseCategory(false);
        setQuery("");
        setCategory("");
        queryRef.current.value = "";
        setSongList([]);
        setCategoryName("");
      };

      document.body.addEventListener("click", handleClick);
      // Cleanup function - runs when effect re-runs or component unmounts
      return () => {
        document.body.removeEventListener("click", handleClick);
      };
    }
  }, [isOpen, categoryPrompt, chooseCategory]);

  const cancelCategories = (e: any) => {
    e.stopPropagation();
    setCategoryName("");
    setCategory("");
    if (queryRef.current.value !== "") {
      setQuery(queryRef.current.value);
    } else {
      setQuery("");
      setIsOpen(false);
      setChoseCategory(true);
    }
  };

  return (
    <header className="row">
      <div className="col-6 col-lg-3 mb-3">
        <div className="d-flex align-items-center">
          <a onClick={goHome}>
            <img
              src="/images/header-logo.png"
              className="img-logo"
              alt="logo"
            />
          </a>
          <div className="text-logo">
            <p className="title text-uppercase">Aryn-lyrics</p>
            <p className="sub-title">Твій збірник пісень</p>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6 order-3 order-lg-2 mb-3">
        <div className="h-100 d-flex align-items-center">
          <div className="songbook-input-container">
            <div className="songbook-input-container-spinner">
              {isLoading ? <Spinner /> : ""}
            </div>
            <input
              type="text"
              ref={queryRef}
              className="songbook-input"
              placeholder="Знайти пісню на сайті. . ."
              onChange={deferredSetQueryHandler}
              onClick={(e) => {
                {
                  setCategoryPrompt(true);
                  e.stopPropagation();
                }
              }}
            />
            <IoSearchOutline className="search" />
            {categoryPrompt ? (
              <div
                className="category-prompt"
                onClick={(e) => {
                  setChoseCategory(true);
                  e.stopPropagation();
                }}
              >
                <p className="btn-mcolor category-prompt-btn">
                  Шукати за катерогіями
                </p>
              </div>
            ) : null}
            {chooseCategory ? (
              <div
                className="choose-category"
                onClick={(e) => e.stopPropagation()}
              >
                <IoCloseCircleOutline
                  className="close"
                  onClick={() => {
                    setChoseCategory(false);
                  }}
                />
                {categoryList?.map((item: any) => (
                  <p
                    key={item.id}
                    onClick={() => {
                      setCategory(item.id.toString());
                      setCategoryName(item.name);
                    }}
                  >
                    {item.name}
                  </p>
                ))}
              </div>
            ) : null}

            {songList?.length > 0 && isOpen && (
              <div className="select-container">
                <IoCloseCircleOutline
                  className="close"
                  onClick={() => {
                    setIsOpen(false);
                    setCategoryPrompt(false);
                    setChoseCategory(false);
                    setQuery("");
                    setCategory("");
                    queryRef.current.value = "";
                    setSongList([]);
                    setCategoryName("");
                  }}
                />
                {categoryName ? (
                  <div>
                    <p
                      className="select-container-cancel_categories"
                      onClick={(e) => cancelCategories(e)}
                    >
                      Скинути категорії
                    </p>
                    <p className="select-container-category_name">
                      {categoryName}
                    </p>
                  </div>
                ) : null}
                {songList?.map((song: any, index: number) => (
                  <div
                    className="select-item"
                    key={index}
                    onClick={() => selectSongbook(song.songbookId, song.id)}
                  >
                    <p>{song.title}</p>{" "}
                    <p className="select-item-songbook">
                      &nbsp;&nbsp;&nbsp;
                      {song.songbook.title}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-6 col-lg-3 order-2 order-lg-3 mb-3">
        <div className="h-100 d-flex justify-content-end align-items-center">
          <div className="manual-tooltip">
            <IoBookOutline
              className="manual-icon"
              onClick={() => navigate("/manual")}
            />
            <span className="manual-tooltip-text">Мануал</span>
          </div>
          {!person ? (
            <button
              className="signIn btn-plain"
              onClick={() => navigate("/loginmodal")}
            >
              Увійти
            </button>
          ) : (
            <div className="logedIn">
              <div className="login-box">
                <div className="login-box-tooltip">
                  <button className="login" onClick={goToCabinet}>
                    <IoPersonSharp className="login-icon" />
                  </button>
                  <span className="login-box-tooltip-text">Кабінет</span>
                </div>
                <div
                  className="user-email"
                  onClick={() => navigate("/loginmodal")}
                >
                  <div>
                    {
                      JSON.parse(localStorage.getItem("User") || "{}")
                        ?.firstName
                    }
                  </div>
                  <div>
                    {JSON.parse(localStorage.getItem("User") || "{}")?.lastName}
                  </div>
                </div>
              </div>
              <div className="exit-tooltip">
                <IoExitOutline
                  className="exit"
                  onClick={() => {
                    localStorage.removeItem("User");
                    localStorage.removeItem("Token");
                    setPerson(false);
                    goHome();
                  }}
                />
                <span className="exit-tooltip-text">Вийти</span>
              </div>
            </div>
          )}
        </div>
        {/* {loginModalShow ?
          <div>
            <LoginModal closeModal={closeLoginModal} />
          </div> : null
        } */}
      </div>
    </header>
  );
};

export default Header;
